import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/shared/auth/useAuth";
import { Button } from "@/shared/components/ui/button";
import type { Role } from "@/shared/types/role";

// Firebase normally resolves onIdTokenChanged in <1s. If we're still "loading"
// after this long, something is wrong (slow network, extension, blocked
// request). We surface a retry UI instead of silently redirecting to /login —
// an unexpected bounce-to-login is the worst UX we can ship for an admin tool.
const LOADING_STUCK_THRESHOLD_MS = 12_000;

interface RequireRoleProps {
  roles: readonly Role[];
  children: ReactNode;
}

export function RequireRole({ roles, children }: RequireRoleProps) {
  const { status, user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (status !== "loading") {
      setStuck(false);
      return;
    }
    const timer = setTimeout(() => setStuck(true), LOADING_STUCK_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [status]);

  if (status === "loading") {
    if (stuck) {
      return (
        <div
          data-testid="auth-stuck"
          className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
        >
          <p className="max-w-md text-muted-foreground">{t("auth.stuckBody")}</p>
          <Button
            data-testid="auth-stuck-retry"
            variant="outline"
            onClick={() => window.location.reload()}
          >
            {t("auth.stuckRetry")}
          </Button>
        </div>
      );
    }
    return (
      <div
        data-testid="auth-loading"
        className="flex min-h-screen items-center justify-center text-muted-foreground"
      >
        {t("auth.loading")}
      </div>
    );
  }

  if (status === "unauthenticated" || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.role || !roles.includes(user.role)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}
